import { withExportDate } from "../../../../utils/exportFileName";

type IssueNode = {
  key: string;
  label: string;
  children?: IssueNode[];
};

type IssueSelection =
  | Record<string, { checked?: boolean; partialChecked?: boolean }>
  | null
  | undefined;

type SelectedIssue = { key: string; label: string };

const AD_SETTINGS_KEYS = new Set([
  "missingPreRoll",
  "missingMidRolls",
  "missingPostRolls",
  "midRollFrequency",
  "midRollInterval",
  "autoGeneratedAds",
]);

const AD_SETTINGS_LABEL = "Ad Settings";

// Beyond this many named issues the file name gets unreadably long.
const MAX_NAMED_ISSUES = 2;

const isChecked = (selection: IssueSelection, key: string) =>
  selection?.[key]?.checked === true;

const collectSelectedIssues = (
  nodes: IssueNode[],
  selection: IssueSelection,
  parentLabel?: string
): SelectedIssue[] =>
  nodes.flatMap((node) => {
    if (node.children?.length) {
      if (isChecked(selection, node.key)) {
        return [{ key: node.key, label: node.label }];
      }
      return collectSelectedIssues(node.children, selection, node.label);
    }
    if (!isChecked(selection, node.key)) return [];
    return [
      {
        key: node.key,
        label: parentLabel ? `${parentLabel} ${node.label}` : node.label,
      },
    ];
  });

/**
 * Builds the Excel export name from the "Select Issue" selection, e.g.
 * "Custom ID Audit - 2026-10-29". Two or more ad issues collapse into
 * "Ad Settings"; with no issue selected the tab's fallback name is used.
 */
export const buildIssueExportName = (
  selection: IssueSelection,
  issueTree: IssueNode[] | undefined,
  fallbackName: string
): string => {
  const selected = collectSelectedIssues(issueTree ?? [], selection);
  const adIssueCount = selected.filter((issue) =>
    AD_SETTINGS_KEYS.has(issue.key)
  ).length;

  const segments =
    adIssueCount > 1
      ? [
          ...selected
            .filter((issue) => !AD_SETTINGS_KEYS.has(issue.key))
            .map((issue) => issue.label),
          AD_SETTINGS_LABEL,
        ]
      : selected.map((issue) => issue.label);

  let baseName: string;
  if (segments.length === 0) {
    baseName = fallbackName;
  } else if (segments.length <= MAX_NAMED_ISSUES) {
    baseName = `${segments.join(" & ")} Audit`;
  } else {
    baseName = `${fallbackName} - Multiple Issues`;
  }

  return withExportDate(baseName);
};
